import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PublicLayout from '../../layouts/PublicLayout';
import publicBookingApi from '../../services/publicBookingApi';
import { getErrorMessageWithDates, formatBlockedDates } from '../../utils/errorMessages';
import CustomDatePicker from '../../components/CustomDatePicker';
import { formatDate } from '../../utils/dateFormatter';
import { toLocalIso } from '../../utils/dateValueHelper';
import '../../styles/BookingPage.css';

/**
 * BookingPage - Main public booking page
 * Route: /book/:slug (slug is actually propertyId - MongoDB ObjectId)
 *
 * Layout matches Gathern design:
 * - Desktop: Sticky booking card on right, content on left
 * - Mobile: Stacked layout with sticky bottom button
 */
const BookingPage = () => {
  const { slug: propertyId } = useParams(); // slug param is actually MongoDB ID
  const navigate = useNavigate();

  // Language state (default Arabic)
  // eslint-disable-next-line no-unused-vars
  const [lang, setLang] = useState('ar'); // setLang reserved for future language switcher

  // Property data
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Booking form state
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [guests, setGuests] = useState(1);
  const [pricing, setPricing] = useState(null);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [availabilityError, setAvailabilityError] = useState(null);
  const [blockedDates, setBlockedDates] = useState([]);
  const [errorSuggestion, setErrorSuggestion] = useState(null);

  // Gallery state (null = show primary photo, number = show gallery index)
  const [selectedImage, setSelectedImage] = useState(null);

  // Reviews state
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState(null);
  const [reviewsLoading, setReviewsLoading] = useState(false);

  const loadProperty = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await publicBookingApi.getProperty(propertyId, lang);

      if (response.success) {
        setProperty(response.property);
      } else {
        setError(response.error || 'فشل في تحميل العقار');
      }
    } catch (err) {
      console.error('Error loading property:', err);
      setError('حدث خطأ في تحميل بيانات العقار');
    } finally {
      setLoading(false);
    }
  }, [propertyId, lang]);

  // Load reviews and stats
  const loadReviews = useCallback(async () => {
    if (!propertyId) return;

    try {
      setReviewsLoading(true);

      // Load reviews and stats in parallel
      const [reviewsResponse, statsResponse] = await Promise.all([
        publicBookingApi.getPropertyReviews(propertyId, { page: 1, limit: 5, lang }),
        publicBookingApi.getPropertyReviewStats(propertyId, lang)
      ]);

      if (reviewsResponse.success) {
        setReviews(reviewsResponse.reviews || []);
      }

      if (statsResponse.success) {
        setReviewStats(statsResponse.stats);
      }
    } catch (err) {
      console.error('Error loading reviews:', err);
      // Fail silently - reviews are not critical
    } finally {
      setReviewsLoading(false);
    }
  }, [propertyId, lang]);

  // Fetch property data on mount
  useEffect(() => {
    loadProperty();
  }, [loadProperty]);

  // Fetch reviews after property loads
  useEffect(() => {
    if (property) {
      loadReviews();
    }
  }, [property, loadReviews]);

  const checkAvailability = useCallback(async () => {
    try {
      setCheckingAvailability(true);
      setAvailabilityError(null); // Clear previous errors
      const response = await publicBookingApi.checkAvailability(
        propertyId,
        { checkIn, checkOut, guests },
        lang
      );

      if (response.success && response.available) {
        setPricing(response.rate);
        setAvailabilityError(null);
        setBlockedDates([]);
        setErrorSuggestion(null);
      } else {
        setPricing(null);

        // Use centralized error message translation with structured data
        const errorData = getErrorMessageWithDates(
          response.errorCode,
          response.errorData || {},
          response.blockedDates || [],
          lang
        );

        setAvailabilityError(errorData.message);
        setBlockedDates(errorData.blockedDates);
        setErrorSuggestion(errorData.suggestion);
      }
    } catch (err) {
      console.error('Error checking availability:', err);
      setPricing(null);
      setBlockedDates([]);
      setErrorSuggestion(null);
      setAvailabilityError(
        lang === 'ar' ? 'حدث خطأ في التحقق من التوافر' : 'Error checking availability'
      );
    } finally {
      setCheckingAvailability(false);
    }
  }, [propertyId, checkIn, checkOut, guests, lang]);

  // Check availability when dates change
  useEffect(() => {
    if (checkIn && checkOut && property) {
      checkAvailability();
    } else {
      // Clear error and pricing when dates incomplete
      setAvailabilityError(null);
      setPricing(null);
      setBlockedDates([]);
      setErrorSuggestion(null);
    }
  }, [checkIn, checkOut, property, checkAvailability]);

  const handleBookNow = () => {
    // Navigate to checkout page with booking data
    navigate(`/book/${propertyId}/checkout`, {
      state: {
        propertyId,
        property,
        checkIn,
        checkOut,
        guests,
        pricing
      }
    });
  };

  // Loading state
  if (loading) {
    return (
      <PublicLayout>
        <div className="booking-page-loading">
          <div className="spinner"></div>
          <p>جاري تحميل العقار...</p>
        </div>
      </PublicLayout>
    );
  }

  // Error state
  if (error) {
    return (
      <PublicLayout>
        <div className="booking-page-error">
          <i className="bi bi-exclamation-circle"></i>
          <h2>عذراً، حدث خطأ</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/')} className="btn-back">
            العودة للرئيسية
          </button>
        </div>
      </PublicLayout>
    );
  }

  // Property not found
  if (!property) {
    return (
      <PublicLayout>
        <div className="booking-page-error">
          <i className="bi bi-search"></i>
          <h2>العقار غير موجود</h2>
          <p>لم نتمكن من العثور على العقار المطلوب</p>
        </div>
      </PublicLayout>
    );
  }

  // Get property data (with fallbacks) - Use complete bookingPageSettings structure
  const bookingSettings = property.bookingPageSettings || {};

  const photos = Array.isArray(bookingSettings.gallery) ? bookingSettings.gallery : [];
  const primaryPhoto = bookingSettings.primaryPhoto || photos[0];

  // Use top-level convenience fields (language-aware from backend)
  const description = (property.description || '').trim();
  const houseRules = Array.isArray(property.houseRules) ? property.houseRules : [];
  const importantInformation = (property.importantInformation || '').trim();

  // Location details
  const neighborhood = (bookingSettings.location?.neighborhood || '').trim() || null;

  return (
    <PublicLayout>
      <div className="booking-page">
        <div className="booking-container">
          {/* Main Content (Left Side) */}
          <div className="booking-content">
            {/* Property Header */}
            <div className="property-header">
              <h1 className="property-title">{property.name}</h1>
              <div className="property-meta">
                <span className="property-location">
                  <i className="bi bi-geo-alt"></i>
                  {property.city || property.address}
                </span>
              </div>
            </div>

            {/* Photo Gallery */}
            <div className="property-gallery">
              <div className="gallery-main">
                <img
                  src={selectedImage !== null ? photos[selectedImage] : (primaryPhoto || photos[0] || '/placeholder.jpg')}
                  alt={property.name}
                  className="gallery-main-image"
                />
              </div>
              {(primaryPhoto || photos.length > 0) && (
                <div className="gallery-thumbnails">
                  {/* Show primary photo thumbnail if it exists */}
                  {primaryPhoto && (
                    <img
                      src={primaryPhoto}
                      alt={`${property.name} - Primary`}
                      className={`gallery-thumbnail ${selectedImage === null ? 'active' : ''}`}
                      onClick={() => setSelectedImage(null)}
                    />
                  )}
                  {/* Show gallery thumbnails (max 6 if primary exists, 7 if not) */}
                  {photos.slice(0, primaryPhoto ? 6 : 7).map((photo, index) => (
                    <img
                      key={index}
                      src={photo}
                      alt={`${property.name} - ${index + 1}`}
                      className={`gallery-thumbnail ${selectedImage === index ? 'active' : ''}`}
                      onClick={() => setSelectedImage(index)}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Description */}
            {description && (
              <div className="property-section">
                <h2 className="section-title">الوصف</h2>
                <p className="property-description">{description}</p>
              </div>
            )}

            {/* Check-in/Check-out Times */}
            <div className="property-section">
              <h2 className="section-title">أوقات تسجيل الوصول والمغادرة</h2>
              <div style={{display: 'flex', gap: '32px', flexWrap: 'wrap'}}>
                <div>
                  <div style={{fontSize: '0.9rem', color: '#666', marginBottom: '4px'}}>
                    <i className="bi bi-clock" style={{marginLeft: '6px'}}></i>
                    تسجيل الوصول
                  </div>
                  <div style={{fontSize: '1.1rem', fontWeight: '600'}}>
                    {bookingSettings.checkInTime || '15:00'}
                  </div>
                </div>
                <div>
                  <div style={{fontSize: '0.9rem', color: '#666', marginBottom: '4px'}}>
                    <i className="bi bi-clock" style={{marginLeft: '6px'}}></i>
                    تسجيل المغادرة
                  </div>
                  <div style={{fontSize: '1.1rem', fontWeight: '600'}}>
                    {bookingSettings.checkOutTime || '11:00'}
                  </div>
                </div>
              </div>
            </div>

            {/* Structured Amenities */}
            {property.amenities && (
              <div className="property-section">
                <h2 className="section-title">المرافق التفصيلية</h2>

                {/* WiFi */}
                {property.amenities.wifi?.available && (
                  <div className="amenity-detail mb-3">
                    <h5>
                      <i className="bi bi-wifi" style={{marginLeft: '8px'}}></i>
                      واي فاي (WiFi)
                    </h5>
                    {property.amenities.wifi.networkName && (
                      <p className="mb-1">
                        <strong>اسم الشبكة:</strong> <code>{property.amenities.wifi.networkName}</code>
                      </p>
                    )}
                    {property.amenities.wifi.password && (
                      <p className="mb-1">
                        <strong>كلمة المرور:</strong> <code>{property.amenities.wifi.password}</code>
                      </p>
                    )}
                    {property.amenities.wifi.instructionsAr && (
                      <p className="mb-0 text-muted">{property.amenities.wifi.instructionsAr}</p>
                    )}
                  </div>
                )}

                {/* Parking */}
                {property.amenities.parking?.available && (
                  <div className="amenity-detail mb-3">
                    <h5>
                      <i className="bi bi-car-front" style={{marginLeft: '8px'}}></i>
                      موقف سيارات
                    </h5>
                    {property.amenities.parking.type && property.amenities.parking.type !== 'none' && (
                      <p className="mb-1">
                        <strong>النوع:</strong>{' '}
                        {property.amenities.parking.type === 'free' && 'مجاني'}
                        {property.amenities.parking.type === 'paid' && 'مدفوع'}
                        {property.amenities.parking.type === 'street' && 'في الشارع'}
                        {property.amenities.parking.type === 'garage' && 'جراج'}
                      </p>
                    )}
                    {property.amenities.parking.instructionsAr && (
                      <p className="mb-0 text-muted">{property.amenities.parking.instructionsAr}</p>
                    )}
                  </div>
                )}

                {/* Pool */}
                {property.amenities.pool?.available && (
                  <div className="amenity-detail mb-3">
                    <h5>
                      <i className="bi bi-water" style={{marginLeft: '8px'}}></i>
                      مسبح
                    </h5>
                    {property.amenities.pool.hours && (
                      <p className="mb-1">
                        <strong>ساعات العمل:</strong> {property.amenities.pool.hours}
                      </p>
                    )}
                    {property.amenities.pool.locationAr && (
                      <p className="mb-0 text-muted">
                        <strong>الموقع:</strong> {property.amenities.pool.locationAr}
                      </p>
                    )}
                  </div>
                )}

                {/* Gym */}
                {property.amenities.gym?.available && (
                  <div className="amenity-detail mb-3">
                    <h5>
                      <i className="bi bi-heart-pulse" style={{marginLeft: '8px'}}></i>
                      صالة رياضية
                    </h5>
                    {property.amenities.gym.hours && (
                      <p className="mb-1">
                        <strong>ساعات العمل:</strong> {property.amenities.gym.hours}
                      </p>
                    )}
                    {property.amenities.gym.locationAr && (
                      <p className="mb-0 text-muted">
                        <strong>الموقع:</strong> {property.amenities.gym.locationAr}
                      </p>
                    )}
                  </div>
                )}

                {/* Simple Amenities */}
                <div className="amenities-grid mt-3">
                  {property.amenities.elevator && (
                    <div className="amenity-item">
                      <i className="bi bi-arrow-up-circle"></i>
                      <span>مصعد</span>
                    </div>
                  )}
                  {property.amenities.airConditioning && (
                    <div className="amenity-item">
                      <i className="bi bi-snow"></i>
                      <span>تكييف</span>
                    </div>
                  )}
                  {property.amenities.heating && (
                    <div className="amenity-item">
                      <i className="bi bi-thermometer-sun"></i>
                      <span>تدفئة</span>
                    </div>
                  )}
                  {property.amenities.washingMachine && (
                    <div className="amenity-item">
                      <i className="bi bi-circle"></i>
                      <span>غسالة</span>
                    </div>
                  )}
                  {property.amenities.dryer && (
                    <div className="amenity-item">
                      <i className="bi bi-circle-fill"></i>
                      <span>نشافة</span>
                    </div>
                  )}
                  {property.amenities.kitchen?.available && (
                    <div className="amenity-item">
                      <i className="bi bi-cup-hot"></i>
                      <span>
                        {property.amenities.kitchen.type === 'full' && 'مطبخ كامل'}
                        {property.amenities.kitchen.type === 'kitchenette' && 'مطبخ صغير'}
                        {!property.amenities.kitchen.type && 'مطبخ'}
                      </span>
                    </div>
                  )}
                  {property.amenities.tv && (
                    <div className="amenity-item">
                      <i className="bi bi-tv"></i>
                      <span>تلفاز</span>
                    </div>
                  )}
                  {property.amenities.workspace && (
                    <div className="amenity-item">
                      <i className="bi bi-laptop"></i>
                      <span>مكتب عمل</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Location (if neighborhood is provided) */}
            {neighborhood && (
              <div className="property-section">
                <h2 className="section-title">الموقع</h2>
                <p className="property-description">
                  <i className="bi bi-geo-alt-fill" style={{marginLeft: '8px'}}></i>
                  {neighborhood}
                </p>
              </div>
            )}

            {/* Important Information */}
            {importantInformation && (
              <div className="property-section">
                <h2 className="section-title">معلومات مهمة</h2>
                <p className="property-description">{importantInformation}</p>
              </div>
            )}

            {/* House Rules */}
            {houseRules.length > 0 && (
              <div className="property-section">
                <h2 className="section-title">قواعد المنزل</h2>
                <ul className="house-rules-list">
                  {houseRules.map((rule, index) => (
                    <li key={index}>{rule}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Cancellation Policy */}
            <div className="property-section">
              <h2 className="section-title">سياسة الإلغاء</h2>
              <p className="cancellation-policy">
                {bookingSettings.cancellationPolicy?.type === 'flexible' &&
                  'سياسة مرنة: استرداد كامل حتى 7 أيام قبل الوصول'}
                {bookingSettings.cancellationPolicy?.type === 'moderate' &&
                  'سياسة معتدلة: استرداد كامل حتى 14 يوم قبل الوصول، استرداد 50% حتى 7 أيام'}
                {bookingSettings.cancellationPolicy?.type === 'strict' &&
                  'سياسة صارمة: استرداد 50% حتى 30 يوم قبل الوصول'}
                {!bookingSettings.cancellationPolicy?.type &&
                  'سياسة معتدلة: استرداد كامل حتى 14 يوم قبل الوصول، استرداد 50% حتى 7 أيام'}
              </p>
            </div>

            {/* Reviews Section - Always visible */}
            <div className="property-section">
              <h2 className="section-title">التقييمات</h2>

              {reviewsLoading ? (
                <div style={{textAlign: 'center', padding: '40px', color: '#666'}}>
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">جاري التحميل...</span>
                  </div>
                </div>
              ) : reviewStats && reviewStats.totalReviews > 0 ? (
                <>
                  {/* Review Stats Header */}
                  <div style={{display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px'}}>
                    <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                      <i className="bi bi-star-fill" style={{color: '#FFD700', fontSize: '1.2rem'}}></i>
                      <span style={{fontSize: '1.2rem', fontWeight: '600'}}>
                        {reviewStats.averageRating.toFixed(1)}
                      </span>
                      <span style={{color: '#666'}}>
                        ({reviewStats.totalReviews} {reviewStats.totalReviews === 1 ? 'تقييم' : 'تقييمات'})
                      </span>
                    </div>
                  </div>

                  {/* Reviews List */}
                  <div style={{display: 'flex', flexDirection: 'column', gap: '24px'}}>
                    {reviews.map((review, index) => (
                      <div key={index} style={{
                        padding: '20px',
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        backgroundColor: '#fafafa'
                      }}>
                        {/* Review Header */}
                        <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '12px'}}>
                          <div>
                            <div style={{fontWeight: '600', marginBottom: '4px'}}>
                              {review.guestName}
                            </div>
                            <div style={{fontSize: '0.85rem', color: '#666'}}>
                              {formatDate(review.createdAt)}
                            </div>
                          </div>
                          <div style={{display: 'flex', alignItems: 'center', gap: '4px'}}>
                            {[...Array(5)].map((_, i) => (
                              <i
                                key={i}
                                className={i < review.rating ? 'bi bi-star-fill' : 'bi bi-star'}
                                style={{color: i < review.rating ? '#FFD700' : '#ccc', fontSize: '1rem'}}
                              ></i>
                            ))}
                          </div>
                        </div>

                        {/* Review Comment */}
                        <p style={{margin: '0 0 12px 0', lineHeight: '1.6'}}>
                          {review.comment}
                        </p>

                        {/* Owner Response */}
                        {review.response && review.response.text && (
                          <div style={{
                            marginTop: '12px',
                            padding: '12px',
                            backgroundColor: '#fff',
                            borderRight: '3px solid #007bff',
                            borderRadius: '4px'
                          }}>
                            <div style={{fontWeight: '600', fontSize: '0.9rem', marginBottom: '6px'}}>
                              رد المالك:
                            </div>
                            <p style={{margin: 0, fontSize: '0.9rem', lineHeight: '1.5'}}>
                              {review.response.text}
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div style={{
                  textAlign: 'center',
                  padding: '60px 20px',
                  backgroundColor: '#fafafa',
                  borderRadius: '8px',
                  border: '1px solid #e0e0e0'
                }}>
                  <i className="bi bi-star" style={{fontSize: '3rem', color: '#ddd', marginBottom: '16px'}}></i>
                  <p style={{fontSize: '1.1rem', color: '#666', margin: 0}}>
                    لا توجد تقييمات بعد
                  </p>
                  <p style={{fontSize: '0.9rem', color: '#999', marginTop: '8px'}}>
                    كن أول من يقيّم هذا العقار!
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Booking Card (Right Side - Sticky) */}
          <div className="booking-sidebar">
            <div className="booking-card">
              {/* Price Display - Only show after availability check (Airbnb pattern) */}
              {!checkIn || !checkOut ? (
                <div className="price-section">
                  <div className="price-display" style={{fontSize: '1.1rem', color: '#666'}}>
                    <span>أدخل التواريخ لمعرفة السعر</span>
                  </div>
                </div>
              ) : availabilityError ? (
                <div className="price-section">
                  <div className="alert alert-warning" style={{textAlign: 'center', padding: '12px', margin: '10px 0'}}>
                    <i className="bi bi-exclamation-triangle" style={{marginLeft: '8px'}}></i>
                    <div>{availabilityError}</div>

                    {/* Show blocked dates with strikethrough (only if 2+ dates to avoid redundancy) */}
                    {blockedDates.length > 1 && (
                      <div style={{marginTop: '8px', fontSize: '0.9rem'}}>
                        <div style={{fontWeight: 'bold', marginBottom: '4px'}}>
                          {lang === 'ar' ? 'التواريخ غير المتاحة:' : 'Unavailable dates:'}
                        </div>
                        <div style={{display: 'flex', flexWrap: 'wrap', gap: '4px', justifyContent: 'center'}}>
                          {blockedDates.map((date, index) => (
                            <span key={index} style={{textDecoration: 'line-through', opacity: 0.7}}>
                              {formatBlockedDates([date], lang)}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Suggestion */}
                    {errorSuggestion && (
                      <div style={{marginTop: '8px', fontSize: '0.85rem', fontStyle: 'italic'}}>
                        {errorSuggestion}
                      </div>
                    )}
                  </div>
                </div>
              ) : pricing ? (
                <div className="price-section">
                  <div className="price-display">
                    <span className="price-amount">{pricing.averageRate}</span>
                    <span className="price-currency">ريال</span>
                    <span className="price-period">/ليلة</span>
                  </div>
                  <small className="text-muted" style={{display: 'block', textAlign: 'center', marginTop: '-8px'}}>
                    متوسط السعر لـ {pricing.nights} {pricing.nights === 1 ? 'ليلة' : 'ليالي'}
                  </small>
                </div>
              ) : checkingAvailability ? (
                <div className="price-section">
                  <div className="price-display" style={{fontSize: '1.1rem', color: '#666'}}>
                    <span>جاري التحقق...</span>
                  </div>
                </div>
              ) : null}

              {/* Date Selection */}
              <div className="booking-dates">
                <div className="date-input-group">
                  <label>تاريخ الوصول</label>
                  <CustomDatePicker
                    propertyId={propertyId}
                    lang={lang}
                    selectedDate={checkIn ? new Date(checkIn) : null}
                    onChange={(date) => {
                      if (date) {
                        const dateStr = toLocalIso(date);
                        setCheckIn(dateStr);
                        // Clear check-out if it's before new check-in
                        if (checkOut && dateStr >= checkOut) {
                          setCheckOut('');
                        }
                      } else {
                        setCheckIn('');
                      }
                    }}
                    minDate={new Date()}
                    placeholderText={lang === 'ar' ? 'اختر تاريخ الوصول' : 'Select check-in date'}
                  />
                </div>
                <div className="date-input-group">
                  <label>تاريخ المغادرة</label>
                  <CustomDatePicker
                    propertyId={propertyId}
                    lang={lang}
                    selectedDate={checkOut ? new Date(checkOut) : null}
                    onChange={(date) => {
                      if (date) {
                        setCheckOut(toLocalIso(date));
                      } else {
                        setCheckOut('');
                      }
                    }}
                    minDate={checkIn ? new Date(new Date(checkIn).getTime() + 86400000) : new Date()}
                    placeholderText={lang === 'ar' ? 'اختر تاريخ المغادرة' : 'Select check-out date'}
                  />
                </div>
              </div>

              {/* Guests Selection */}
              <div className="guests-section">
                <label>عدد الضيوف</label>
                <select
                  value={guests}
                  onChange={(e) => setGuests(parseInt(e.target.value))}
                  className="guests-select"
                >
                  {[1, 2, 3, 4, 5, 6].map(n => (
                    <option key={n} value={n}>{n} {n === 1 ? 'ضيف' : 'ضيوف'}</option>
                  ))}
                </select>
              </div>

              {/* Price Breakdown */}
              {pricing && (
                <div className="price-breakdown">
                  <div className="price-row">
                    <span>الإجمالي الفرعي</span>
                    <span>{pricing.subtotal} ريال</span>
                  </div>
                  <div className="price-row">
                    <span>الضريبة (15%)</span>
                    <span>{pricing.tax} ريال</span>
                  </div>
                  <hr className="price-divider" />
                  <div className="price-row total">
                    <span>الإجمالي</span>
                    <span>{pricing.total} ريال</span>
                  </div>
                </div>
              )}

              {/* Book Button */}
              <button
                onClick={handleBookNow}
                disabled={!checkIn || !checkOut || !pricing || checkingAvailability}
                className="btn-book-now"
              >
                {checkingAvailability ? 'جاري التحقق...' : 'احجز الآن'}
              </button>

              {/* Trust Badge */}
              <div className="trust-badge">
                <i className="bi bi-shield-check"></i>
                <span>ضمان Host Me</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
};

export default BookingPage;
